// Log Collection System
// Stores errors and console logs in browser localStorage for debugging
// Requires user consent before collecting any data (GDPR/CCPA compliant)

import consentManager from './consentManager';

const LOG_STORAGE_KEY = 'app_error_logs';
const MAX_LOG_ENTRIES = 100; // Limit to prevent storage overflow

class LogCollector {
  constructor() {
    this.logs = this.loadLogs();
    this.consentGranted = false;
    this.originalConsoleMethods = {};
    this.errorHandler = null;
    this.rejectionHandler = null;
    this.checkConsentAndSetup();
  }

  // Check consent before setting up interception
  checkConsentAndSetup() {
    try {
      this.consentGranted = consentManager.hasConsent();
      
      if (this.consentGranted) {
        this.setupConsoleInterception();
        this.setupErrorHandling();
      }
    } catch (error) {
      // Silently fail - don't break the app if consent check fails
      this.consentGranted = false;
    }
  }

  // Check if consent has been granted (for external calls)
  hasConsent() {
    return this.consentGranted;
  }

  // Initialize logging after consent is granted
  initializeWithConsent() {
    if (!this.consentGranted) {
      this.consentGranted = true;
      this.setupConsoleInterception();
      this.setupErrorHandling();
    }
  }

  // Stop logging if consent is revoked
  revokeLogging() {
    this.consentGranted = false;
    this.restoreConsoleMethods();
    this.removeErrorHandling();
    this.clearLogs();
  }

  // Load existing logs from localStorage
  loadLogs() {
    try {
      // Check if localStorage is available
      if (typeof window === 'undefined' || !window.localStorage) {
        return [];
      }
      const stored = localStorage.getItem(LOG_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      // Silently fail - don't break the app if localStorage isn't available
      return [];
    }
  }

  // Save logs to localStorage
  saveLogs() {
    try {
      // Keep only the most recent logs
      if (this.logs.length > MAX_LOG_ENTRIES) {
        this.logs = this.logs.slice(-MAX_LOG_ENTRIES);
      }
      localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(this.logs));
    } catch (error) {
      console.error('Failed to save logs:', error);
      // If storage is full, clear old logs and try again
      if (error.name === 'QuotaExceededError') {
        this.logs = this.logs.slice(-50);
        try {
          localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(this.logs));
        } catch (e) {
          console.error('Storage still full after cleanup:', e);
        }
      }
    }
  }

  // Add a log entry (only if consent granted)
  addLog(type, message, data = null) {
    if (!this.consentGranted) {
      return null; // Don't log if consent not granted
    }

    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      type, // 'error', 'warn', 'info', 'console'
      message: typeof message === 'string' ? message : String(message),
      data: data ? this.sanitizeData(data) : null,
      url: this.sanitizeUrl(window.location.href),
      userAgent: this.sanitizeUserAgent(navigator.userAgent),
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };

    this.logs.push(logEntry);
    this.saveLogs();
    return logEntry;
  }

  // Sanitize URL to remove query parameters that might contain PII
  sanitizeUrl(url) {
    try {
      const urlObj = new URL(url);
      // Remove query parameters and hash to avoid logging sensitive data
      return `${urlObj.origin}${urlObj.pathname}`;
    } catch (error) {
      // Fallback: just return domain if URL parsing fails
      return url.split('?')[0].split('#')[0];
    }
  }

  // Sanitize user agent to remove potentially identifying information
  sanitizeUserAgent(userAgent) {
    // Only keep browser and OS info, remove version numbers and device IDs
    return userAgent
      .replace(/\([^)]*\)/g, '') // Remove detailed system info in parentheses
      .replace(/\d+\.\d+\.\d+/g, 'x.x.x') // Replace version numbers
      .substring(0, 100); // Limit length
  }

  // Sanitize data to prevent circular references and remove PII
  sanitizeData(data) {
    try {
      // Convert to string and back to remove circular refs
      return JSON.parse(JSON.stringify(data, (key, value) => {
        // Skip functions
        if (typeof value === 'function') {
          return '[Function]';
        }
        
        // Remove potentially sensitive keys
        const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'email', 'phone', 'ssn', 'creditCard'];
        if (typeof key === 'string' && sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
          return '[REDACTED]';
        }
        
        // Limit string length
        if (typeof value === 'string' && value.length > 1000) {
          return value.substring(0, 1000) + '... [truncated]';
        }
        
        return value;
      }));
    } catch (error) {
      return { error: 'Failed to serialize data', message: String(data).substring(0, 200) };
    }
  }

  // Intercept console errors (only if consent granted)
  setupConsoleInterception() {
    if (!this.consentGranted) {
      return;
    }

    // Store original methods for restoration
    this.originalConsoleMethods.error = console.error;
    this.originalConsoleMethods.warn = console.warn;
    this.originalConsoleMethods.info = console.info;

    const self = this;
    console.error = function(...args) {
      self.addLog('error', args.join(' '), args);
      self.originalConsoleMethods.error.apply(console, args);
    };

    console.warn = function(...args) {
      self.addLog('warn', args.join(' '), args);
      self.originalConsoleMethods.warn.apply(console, args);
    };

    console.info = function(...args) {
      self.addLog('info', args.join(' '), args);
      self.originalConsoleMethods.info.apply(console, args);
    };
  }

  // Restore original console methods
  restoreConsoleMethods() {
    if (this.originalConsoleMethods.error) {
      console.error = this.originalConsoleMethods.error;
    }
    if (this.originalConsoleMethods.warn) {
      console.warn = this.originalConsoleMethods.warn;
    }
    if (this.originalConsoleMethods.info) {
      console.info = this.originalConsoleMethods.info;
    }
  }

  // Setup global error handlers (only if consent granted)
  setupErrorHandling() {
    if (!this.consentGranted) {
      return;
    }

    const self = this;
    
    // Catch unhandled errors
    this.errorHandler = (event) => {
      self.addLog('error', event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error ? {
          name: event.error.name,
          message: event.error.message,
          stack: event.error.stack
        } : null
      });
    };

    // Catch unhandled promise rejections
    this.rejectionHandler = (event) => {
      self.addLog('error', 'Unhandled Promise Rejection', {
        reason: event.reason ? String(event.reason) : 'Unknown',
        stack: event.reason?.stack || null
      });
    };

    window.addEventListener('error', this.errorHandler);
    window.addEventListener('unhandledrejection', this.rejectionHandler);
  }

  // Remove error handlers
  removeErrorHandling() {
    if (this.errorHandler) {
      window.removeEventListener('error', this.errorHandler);
    }
    if (this.rejectionHandler) {
      window.removeEventListener('unhandledrejection', this.rejectionHandler);
    }
  }

  // Get all logs
  getAllLogs() {
    return [...this.logs];
  }

  // Get logs filtered by type
  getLogsByType(type) {
    return this.logs.filter(log => log.type === type);
  }

  // Clear all logs
  clearLogs() {
    this.logs = [];
    this.saveLogs();
  }

  // Revoke consent and clean up
  revokeConsent() {
    this.revokeLogging();
  }

  // Export logs as JSON file
  exportLogsAsJSON() {
    const logData = {
      exportDate: new Date().toISOString(),
      totalLogs: this.logs.length,
      logs: this.logs,
      systemInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        screenResolution: {
          width: window.screen.width,
          height: window.screen.height
        },
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        url: window.location.href,
        referrer: document.referrer
      }
    };

    const blob = new Blob([JSON.stringify(logData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Export logs as text file
  exportLogsAsText() {
    let text = 'ERROR LOGS EXPORT\n';
    text += '==================\n\n';
    text += `Export Date: ${new Date().toISOString()}\n`;
    text += `Total Logs: ${this.logs.length}\n\n`;
    text += `System Info:\n`;
    text += `- User Agent: ${navigator.userAgent}\n`;
    text += `- Platform: ${navigator.platform}\n`;
    text += `- URL: ${window.location.href}\n`;
    text += `- Screen: ${window.screen.width}x${window.screen.height}\n`;
    text += `- Viewport: ${window.innerWidth}x${window.innerHeight}\n\n`;
    text += 'LOGS:\n';
    text += '=====\n\n';

    this.logs.forEach((log, index) => {
      text += `[${index + 1}] ${log.type.toUpperCase()} - ${log.timestamp}\n`;
      text += `Message: ${log.message}\n`;
      if (log.data) {
        text += `Data: ${JSON.stringify(log.data, null, 2)}\n`;
      }
      text += `URL: ${log.url}\n`;
      text += '---\n\n';
    });

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Add React error to logs
  addReactError(error, errorInfo) {
    this.addLog('error', error.message || 'React Error', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack || null
    });
  }
}

// Create singleton instance with error handling
let logCollector;
try {
  logCollector = new LogCollector();
} catch (error) {
  // If logger fails to initialize, create a minimal stub
  console.warn('Logger initialization failed:', error);
  logCollector = {
    addLog: () => {},
    addReactError: () => {},
    getAllLogs: () => [],
    clearLogs: () => {},
    exportLogsAsJSON: () => {},
    exportLogsAsText: () => {},
    hasConsent: () => false,
    initializeWithConsent: () => {},
    revokeConsent: () => {}
  };
}

export default logCollector;

